import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@glu/theming';
import { withFormContext } from '@glu/validation-react';
import get from 'lodash/get';

import LocaleContext from '../../locale/LocaleContext';

import { PAYMENT_TYPES } from '../../../constants';
import { maxCharactersTemplate } from '../../gluValidation/utils';
import { lt, regexp } from '../../../utils/validations/gluValidations';
import { getCalculations } from './PaymentInfo';

import Input from '../../gluValidation/Input';
import WireContext from '../WireContext';

const FormContextWrapper = ({ children, data }) => children({ formData: data });
const FormContext = withFormContext(FormContextWrapper);

const styles = () => ({
  exchange: {
    '& .help-block': {
      marginBottom: 0
    }
  },
  labelClass: {
    display: 'block'
  }
});

const useStyles = createUseStyles(styles);

const ExchangeRateComponent = ({ data, formState, onParentUpdate }) => {
  const locale = useContext(LocaleContext);
  const classes = useStyles();
  const wireContextState = useContext(WireContext);

  const id = get(formState.currency, '0.id', get(formState, 'currency.id'));
  const currency = data.fxRates.find((x) => x.id === id);
  const usd = (currency && currency.currency === locale['common.usd']) || id === 1;
  const domestic =
    get(formState, 'paymentType.0.type', get(formState, 'paymentType')) ===
    PAYMENT_TYPES.WIRE.DOMESTIC;

  if (domestic || usd || !formState.currency) return null;
  const defaultExchangeRate = (currency && currency.exchange) || formState.exchangeRate;

  const inputProps = {
    id: 'exchangeRate',
    name: 'exchangeRate',
    label: locale['common.exchangeRate'],
    labelClass: classes.labelClass,
    className: classes.exchange,
    maxLength: 11,
    debounceInput: true,
    defaultValue: defaultExchangeRate,
    validators: [
      lt({ maxLength: 10, msg: maxCharactersTemplate(locale, 10) }),
      // check for field have invalid characters
      regexp({
        regexp: /^((|[0-9])+(|\.[0-9]*)$)/,
        msg: locale['create.payment.validation.invalidCharacterNotAllowed']
      }),
      // check for number is grater than 0 and not empty string
      regexp({
        regexp: /^(?![\s\S])|(?=.*\d)(?=.*[1-9])/,
        msg: locale['create.payment.info.mustBeGrater']
      })
    ],
    onChange: (name, value) => {
      const details = getCalculations({
        ...formState,
        exchangeRate: value
      });
      const { type } = details;
      if (wireContextState && wireContextState.updateData) {
        wireContextState.updateData({ exchangeRate: value });
      }
      onParentUpdate(details[type].sent, get(formState, 'currency.0'));
    },
    helpMsg: locale['create.payment.info.exchangeRateWarning']
  };

  return (
    <FormContext>
      {({ formData }) => {
        const props = {
          ...inputProps,
          defaultValue: formData.exchangeRate
            ? formData.exchangeRate
            : get(formData, 'currency[0].exchange', '')
        };
        // Don't show this field until the glu currency field is defined
        if (!get(formData, 'currency[0].exchange')) return null;
        return (
          <div className="col-xs-3">
            <Input {...props} />
          </div>
        );
      }}
    </FormContext>
  );
};

ExchangeRateComponent.propTypes = {
  data: PropTypes.shape({}).isRequired,
  formState: PropTypes.shape({}).isRequired,
  onParentUpdate: PropTypes.shape({}).isRequired
};

export default ExchangeRateComponent;
